/**
 * 將住宿查詢頁選擇轉成可持久化／日後銜接 `api/cart/add` 的草稿結構。
 * 實際加車 payload 需與後端住宿購物車契約對齊後再補齊。
 *
 * @param {object} payload
 * @param {number} payload.productId
 * @param {string} payload.checkIn
 * @param {string} payload.checkOut 退房日（Y-m-d），與搜尋頁表單一致；呼叫後端 API 時應換算為最後住宿晚
 * @param {number|null} payload.mainCategoryId
 * @param {Array<{ id: string, petCount: number, sizeOptionIds: number[] }>} payload.rooms
 * @param {Record<number, number>} payload.quantityByRoomTypeId
 * @param {Array<object>} payload.roomTypesApi
 */
export function buildHotelCheckoutDraft(payload) {
  return {
    createdAt: new Date().toISOString(),
    ...payload,
    /** 2：checkOut 為退房日；舊版 1 為最後住宿晚 */
    version: 2,
  }
}

export const HOTEL_SEARCH_DRAFT_KEY = 'site.hotel.search.draft'

/** 暫存訂單容器 UUID（換頁／重新整理備援，與網址 query 併用） */
export const HOTEL_TEMP_CONTAINER_UUID_KEY = 'site.hotel.tempOrder.containerUuid'

/** 倒數截止時間戳 ms */
export const HOTEL_TEMP_UNTIL_MS_KEY = 'site.hotel.tempOrder.untilMs'

/** Step1→入住資訊：已選房／寵物指派，待入住頁回存寵物後再 POST step-one */
export const HOTEL_STEP1_ROOMS_PAYLOAD_KEY = 'site.hotel.step1.roomsPayload'

export function saveHotelStep1RoomsPayload(containerUuid, rooms) {
  try {
    sessionStorage.setItem(
      HOTEL_STEP1_ROOMS_PAYLOAD_KEY,
      JSON.stringify({ containerUuid: String(containerUuid), rooms })
    )
  } catch (e) {
    console.warn('saveHotelStep1RoomsPayload failed', e)
  }
}

export function readHotelStep1RoomsPayload(containerUuid) {
  try {
    const raw = sessionStorage.getItem(HOTEL_STEP1_ROOMS_PAYLOAD_KEY)
    if (!raw) return null
    const o = JSON.parse(raw)
    if (!o || String(o.containerUuid) !== String(containerUuid)) return null
    return Array.isArray(o.rooms) ? o.rooms : null
  } catch (e) {
    return null
  }
}

export function clearHotelStep1RoomsPayload() {
  try {
    sessionStorage.removeItem(HOTEL_STEP1_ROOMS_PAYLOAD_KEY)
  } catch (e) {}
}

/**
 * 解析 POST /hotel/temporary-order 成功內容。
 * axios 攔截器通常已回傳 `response.data.data`；若仍帶 JJAJ 外殼 `{ data: { container_uuid } }` 再取內層。
 *
 * @param {object} raw
 * @returns {object}
 */
export function unwrapHotelTemporaryOrderResponse(raw) {
  if (!raw || typeof raw !== 'object') return {}
  if (raw.container_uuid != null || raw.containerUuid != null) return raw
  if (raw.data && typeof raw.data === 'object' && (raw.data.container_uuid != null || raw.data.containerUuid != null)) {
    return raw.data
  }
  return raw
}

/**
 * @param {object} payload unwrap 後的物件
 * @returns {string}
 */
export function getHotelTempOrderContainerUuid(payload) {
  if (!payload || typeof payload !== 'object') return ''
  const id = payload.container_uuid != null ? payload.container_uuid : payload.containerUuid
  return id != null && id !== '' ? String(id) : ''
}

export function saveHotelSearchDraft(draft) {
  try {
    localStorage.setItem(HOTEL_SEARCH_DRAFT_KEY, JSON.stringify(draft))
  } catch (e) {
    console.warn('saveHotelSearchDraft failed', e)
  }
}

/**
 * 住宿容器結帳 POST 需帶貴賓姓名／電話／Email（後端寫入 order_container，會員訂單「預約資訊」區塊）。
 * 已填欄位不覆寫；空欄位依序使用容器 API 資料、登入會員。
 *
 * @param {Record<string, unknown>} submitData
 * @param {Record<string, unknown>|null|undefined} cart
 * @param {Record<string, unknown>|null|undefined} currentMember
 * @returns {Record<string, unknown>}
 */
export function mergeHotelContainerSubmitMemberFields(submitData, cart, currentMember) {
  const base = submitData && typeof submitData === 'object' ? submitData : {}
  const c = cart && typeof cart === 'object' ? cart : {}
  const m = currentMember && currentMember.id ? currentMember : null
  const phoneParts = (m && m.phone != null ? String(m.phone) : '+886-').split('-')
  const mpc = phoneParts[0] && String(phoneParts[0]).trim() ? String(phoneParts[0]).trim() : '+886'
  const mpn = phoneParts.slice(1).join('-').trim()

  const nonempty = (v) => v != null && String(v).trim() !== ''

  let fullName = base.fullName
  if (!nonempty(fullName)) fullName = c.fullName
  if (!nonempty(fullName) && m) fullName = m.fullName

  let gender = base.gender
  if (!nonempty(gender)) gender = c.gender
  if (!nonempty(gender) && m) gender = m.gender
  if (!nonempty(gender)) gender = 'male'

  let phoneCode = base.phoneCode
  if (!nonempty(phoneCode)) phoneCode = c.phoneCode
  if (!nonempty(phoneCode) && m) phoneCode = mpc

  let phoneNumber = base.phoneNumber
  if (!nonempty(phoneNumber)) phoneNumber = c.phoneNumber
  if (!nonempty(phoneNumber) && m) phoneNumber = mpn

  let email = base.email
  if (!nonempty(email)) email = c.email
  if (!nonempty(email) && m) email = m.email

  let note = base.note
  if (!nonempty(note)) note = c.note

  return {
    ...base,
    fullName: nonempty(fullName) ? String(fullName).trim() : '',
    gender: gender || 'male',
    phoneCode: nonempty(phoneCode) ? String(phoneCode).trim() : '+886',
    phoneNumber: nonempty(phoneNumber) ? String(phoneNumber).trim() : '',
    email: nonempty(email) ? String(email).trim() : '',
    note: note != null ? note : '',
  }
}
